import type {
  AmapSearchResult,
  ArchiveCategoryMode,
  ArchiveKind,
  ArchiveOrderLevel,
  ArchiveOrderState,
  OrderArchiveKind,
  AdminMaterial,
  AdminCaseList,
  AdminCaseSummary,
  AdminEnterpriseList,
  AdminLocation,
  AdminUser,
  CatalogStats,
  CaseDetailData,
  CaseList,
  Community,
  ClientConfig,
  EnterpriseDocument,
  EnterpriseDocumentDetail,
  MapLocation,
  MaterialCaseOption,
  MaterialEnterpriseOption,
  SystemStatus,
  SocialAccount,
  User,
  CaseUploadResult,
  CaseAccessResult,
  CaseViewLogPage,
  FloorplanLibraryData,
  FloorplanFile,
  FloorplanKind,
} from "./types";

export class ApiError extends Error {
  status: number;
  retryAfter: number;

  constructor(status: number, message: string, retryAfter = 0) {
    super(message);
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

type UploadProgressHandler = (percent: number) => void;

function getCookie(name: string): string {
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie.split("; ").find((value) => value.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : "";
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = getCookie("effect_archive_csrf");
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  const response = await fetch(path, { ...options, headers, credentials: "include" });
  if (!response.ok) {
    let message = "请求失败，请稍后重试。";
    try {
      const payload = (await response.json()) as { detail?: string };
      if (payload.detail) message = payload.detail;
    } catch {
      // The server may return a plain-text security response.
    }
    throw new ApiError(response.status, message, Number(response.headers.get("Retry-After")) || 0);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function uploadRequest<T>(
  path: string,
  body: FormData,
  onProgress?: UploadProgressHandler,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", path);
    xhr.withCredentials = true;
    const csrf = getCookie("effect_archive_csrf");
    if (csrf) xhr.setRequestHeader("X-CSRF-Token", csrf);

    xhr.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable || event.total <= 0) return;
      const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
      onProgress?.(percent);
    });
    xhr.upload.addEventListener("load", () => onProgress?.(100));
    xhr.addEventListener("load", () => {
      let payload: unknown;
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        payload = undefined;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        const detail = payload && typeof payload === "object" && "detail" in payload
          ? String((payload as { detail?: unknown }).detail ?? "")
          : "";
        reject(new ApiError(xhr.status, detail || "上传失败，请稍后重试。"));
        return;
      }
      onProgress?.(100);
      resolve(payload as T);
    });
    xhr.addEventListener("error", () => {
      reject(new ApiError(0, "网络连接失败，上传未完成，请检查网络后重试。"));
    });
    xhr.addEventListener("abort", () => {
      reject(new ApiError(0, "上传已取消。"));
    });

    onProgress?.(0);
    xhr.send(body);
  });
}

export const api = {
  authStatus: () => request<{ user: User | null }>("/api/auth/status"),
  me: () => request<User>("/api/auth/me"),
  login: (username: string, password: string) =>
    request<{ user: User }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  stats: () => request<CatalogStats>("/api/stats"),
  communities: () => request<Community[]>("/api/communities"),
  materialLibraries: () => request<Community[]>("/api/material-libraries"),
  enterpriseCategories: () => request<Community[]>("/api/enterprise-categories"),
  archiveOrder: (archiveKind: ArchiveKind, level: ArchiveOrderLevel) =>
    request<ArchiveOrderState>(`/api/admin/archive-order/${archiveKind}?level=${level}`),
  updateArchiveOrder: (
    archiveKind: ArchiveKind,
    level: ArchiveOrderLevel,
    ids: number[],
    mode?: ArchiveCategoryMode,
  ) => request<{ archive_kind: ArchiveKind; level: ArchiveOrderLevel; mode: ArchiveCategoryMode; ids: number[] }>(
    `/api/admin/archive-order/${archiveKind}`,
    { method: "PATCH", body: JSON.stringify({ level, ids, mode }) },
  ),
  updateCommunity: (id: number, name: string) => request<{ id: number; name: string; case_count: number }>(
    `/api/admin/communities/${id}`,
    { method: "PATCH", body: JSON.stringify({ name }) },
  ),
  cases: (params: { q?: string; community?: number; page?: number; scope?: "all" | "landed" }) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    if (params.community) search.set("community", String(params.community));
    search.set("scope", params.scope ?? "all");
    search.set("page", String(params.page ?? 1));
    search.set("page_size", "24");
    return request<CaseList>(`/api/cases?${search.toString()}`);
  },
  recentCases: (params: { q?: string; page?: number; pageSize?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    search.set("page", String(params.page ?? 1));
    search.set("page_size", String(params.pageSize ?? 24));
    return request<CaseList>(`/api/cases/recent?${search.toString()}`);
  },
  materialCases: (params: { q?: string; community?: number; page?: number }) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    if (params.community) search.set("community", String(params.community));
    search.set("page", String(params.page ?? 1));
    search.set("page_size", "24");
    return request<CaseList>(`/api/material-cases?${search.toString()}`);
  },
  recentMaterialCases: (params: { q?: string; page?: number; pageSize?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    search.set("page", String(params.page ?? 1));
    search.set("page_size", String(params.pageSize ?? 24));
    return request<CaseList>(`/api/material-cases/recent/list?${search.toString()}`);
  },
  materialCaseDetail: (id: number) => request<CaseDetailData>(`/api/material-cases/${id}`),
  openMaterialCase: (id: number) => request<CaseDetailData>(`/api/material-cases/${id}/view`, { method: "POST" }),
  caseDetail: (id: number) => request<CaseDetailData>(`/api/cases/${id}`),
  openCase: (id: number) => request<CaseDetailData>(
    `/api/cases/${id}/view`,
    { method: "POST" },
  ),
  adminCases: (params: {
    q?: string;
    page?: number;
  }) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    search.set("page", String(params.page ?? 1));
    search.set("page_size", "24");
    return request<AdminCaseList>(`/api/admin/cases?${search.toString()}`);
  },
  adminMaterialCases: (params: { q?: string; page?: number }) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    return request<AdminCaseSummary[]>(`/api/admin/material-cases?${search.toString()}`).then((items) => ({
      items,
      page: params.page ?? 1,
      page_size: Math.max(items.length, 1),
      total: items.length,
    }));
  },
  uploadCase: (values: {
    community: string;
    room: string;
    area: string;
    style: string;
    remark: string;
    files: File[];
    onProgress?: UploadProgressHandler;
  }) => {
    const body = new FormData();
    body.set("community", values.community);
    body.set("room", values.room);
    body.set("area", values.area);
    body.set("style", values.style);
    body.set("remark", values.remark);
    values.files.forEach((file) => body.append("files", file, file.name));
    return uploadRequest<CaseUploadResult>("/api/admin/cases/upload", body, values.onProgress);
  },
  uploadMaterialCase: (values: {
    community: string; room: string; area: string; style: string; remark: string;
    files: File[]; onProgress?: UploadProgressHandler;
  }) => {
    const body = new FormData();
    body.set("library", values.community);
    body.set("room", values.room);
    body.set("specification", values.area);
    body.set("tag", values.style);
    body.set("description", values.remark);
    values.files.forEach((file) => body.append("files", file, file.name));
    return uploadRequest<CaseUploadResult>("/api/admin/material-cases/upload", body, values.onProgress);
  },
  updateCase: (id: number, body: {
    version: number;
    room?: string | null;
    area?: string | null;
    style?: string | null;
    remark?: string | null;
    cover_image_id?: number | null;
    image_ids?: number[];
  }) => request<{ version: number }>(`/api/admin/cases/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  }),
  updateMaterialCase: (id: number, body: {
    version: number; room?: string | null; area?: string | null; style?: string | null;
    remark?: string | null; community?: string | null; cover_image_id?: number | null; image_ids?: number[];
  }) => request<{ version: number }>(`/api/admin/material-cases/${id}`, {
    method: "PATCH",
    body: JSON.stringify({
      version: body.version, room: body.room, library: body.community,
      specification: body.area, tag: body.style, description: body.remark,
      cover_image_id: body.cover_image_id, image_ids: body.image_ids, published: true,
    }),
  }),
  addCaseImages: (id: number, version: number, files: File[], onProgress?: UploadProgressHandler) => {
    const body = new FormData();
    body.set("version", String(version));
    files.forEach((file) => body.append("files", file, file.name));
    return uploadRequest<{ case_id: number; added_count: number; image_ids: number[]; version: number }>(
      `/api/admin/cases/${id}/images`,
      body,
      onProgress,
    );
  },
  addMaterialCaseImages: (id: number, version: number, files: File[], onProgress?: UploadProgressHandler) => {
    const body = new FormData(); body.set("version", String(version));
    files.forEach((file) => body.append("files", file, file.name));
    return uploadRequest<{ collection_id: number; image_ids: number[]; version: number }>(
      `/api/admin/material-cases/${id}/images`, body, onProgress,
    );
  },
  deleteCaseImage: (caseId: number, imageId: number, version: number) => request<{
    case_id: number;
    image_id: number;
    deleted: boolean;
    version: number;
    cover_image_id: number | null;
    cleanup_pending: boolean;
  }>(`/api/admin/cases/${caseId}/images/${imageId}`, {
    method: "DELETE",
    body: JSON.stringify({ version }),
  }),
  deleteMaterialCaseImage: (caseId: number, imageId: number, version: number) => request<{
    image_id: number; deleted: boolean; version: number;
  }>(`/api/admin/material-cases/${caseId}/images/${imageId}`, {
    method: "DELETE", body: JSON.stringify({ version }),
  }),
  deleteCase: (id: number, version: number) => request<{
    id: number;
    deleted: boolean;
    origin: "library" | "managed";
    cleanup_pending: boolean;
  }>(`/api/admin/cases/${id}`, {
    method: "DELETE",
    body: JSON.stringify({ version }),
  }),
  deleteMaterialCase: (id: number, _version: number) => request<{ id: number; deleted: boolean }>(
    `/api/admin/material-cases/${id}`, { method: "DELETE" },
  ),
  adminMaterials: (q = "", scope: "case" | "material" | "enterprise" = "case") => {
    const search = new URLSearchParams();
    if (q) search.set("q", q);
    search.set("scope", scope);
    return request<AdminMaterial[]>(`/api/admin/materials?${search.toString()}`);
  },
  materialCaseOptions: () => request<MaterialCaseOption[]>("/api/admin/material-case-options"),
  materialMaterialOptions: () => request<MaterialCaseOption[]>("/api/admin/material-material-options"),
  materialEnterpriseOptions: () => request<MaterialEnterpriseOption[]>("/api/admin/material-enterprise-options"),
  createLinkMaterial: (body: {
    scope?: "case" | "material" | "enterprise";
    kind: "douyin" | "panorama" | "link";
    label: string;
    url: string;
    preview_url?: string | null;
    case_ids: number[];
    material_case_ids?: number[];
    enterprise_document_ids?: number[];
  }) => request<{ id: number }>("/api/admin/materials/link", {
    method: "POST",
    body: JSON.stringify(body),
  }),
  uploadMaterial: (
    file: File,
    label: string,
    caseIds: number[],
    onProgress?: UploadProgressHandler,
    scope: "case" | "material" | "enterprise" = "case",
    enterpriseDocumentIds: number[] = [],
    materialCaseIds: number[] = [],
  ) => {
    const body = new FormData();
    body.set("file", file);
    body.set("label", label);
    body.set("case_ids", JSON.stringify(caseIds));
    body.set("enterprise_document_ids", JSON.stringify(enterpriseDocumentIds));
    body.set("material_case_ids", JSON.stringify(materialCaseIds));
    body.set("scope", scope);
    return uploadRequest<{ id: number }>("/api/admin/materials/upload", body, onProgress).then(result => {
      if (/\.dwg$/i.test(file.name) && file.size <= 16*1024*1024) window.dispatchEvent(new CustomEvent("cad-browser-request", {detail:{id:`material-${result.id}`,title:label}}));
      return result;
    });
  },
  updateMaterial: (id: number, body: {
    version: number;
    label: string;
    target_url?: string | null;
    preview_url?: string | null;
    case_ids: number[];
    material_case_ids?: number[];
    enterprise_document_ids?: number[];
    published: boolean;
  }) => request<AdminMaterial>(`/api/admin/materials/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  }),
  deleteMaterial: (id: number) => request<{ id: number; deleted: boolean }>(
    `/api/admin/materials/${id}`,
    { method: "DELETE" },
  ),
  enterpriseDocuments: (q = "", category = "") => {
    const search = new URLSearchParams();
    if (q) search.set("q", q);
    if (category) search.set("category", category);
    return request<EnterpriseDocument[]>(`/api/enterprise-documents?${search.toString()}`);
  },
  enterpriseArchive: (params: { q?: string; category?: string; page?: number }) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    if (params.category) search.set("category", params.category);
    search.set("page", String(params.page ?? 1));
    search.set("page_size", "24");
    return request<AdminEnterpriseList>(`/api/enterprise-documents?${search.toString()}`);
  },
  recentEnterpriseDocuments: (params: { q?: string; page?: number; pageSize?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    search.set("page", String(params.page ?? 1));
    search.set("page_size", String(params.pageSize ?? 24));
    return request<AdminEnterpriseList>(`/api/enterprise-documents/recent/list?${search.toString()}`);
  },
  enterpriseDocument: (id: number) => request<EnterpriseDocumentDetail>(`/api/enterprise-documents/${id}`),
  openEnterpriseDocument: (id: number) => request<EnterpriseDocumentDetail>(
    `/api/enterprise-documents/${id}/view`, { method: "POST" },
  ),
  adminEnterpriseDocuments: (params: { q?: string; page?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    search.set("page", String(params.page ?? 1));
    search.set("page_size", "24");
    return request<AdminEnterpriseList>(`/api/admin/enterprise-documents?${search.toString()}`);
  },
  uploadEnterpriseDocument: (values: {
    title: string;
    category: string;
    description: string;
    files: File[];
    onProgress?: UploadProgressHandler;
  }) => {
    const body = new FormData();
    body.set("title", values.title);
    body.set("category", values.category);
    body.set("description", values.description);
    values.files.forEach((file) => body.append("files", file, file.name));
    return uploadRequest<{ id: number; title: string; image_count: number }>(
      "/api/admin/enterprise-documents/upload",
      body,
      values.onProgress,
    );
  },
  updateEnterpriseDocument: (id: number, body: {
    version: number;
    title: string;
    category?: string | null;
    description?: string | null;
    cover_image_id?: number | null;
    image_ids: number[];
    published: boolean;
  }) => request<EnterpriseDocument>(`/api/admin/enterprise-documents/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  }),
  addEnterpriseImages: (
    id: number,
    version: number,
    files: File[],
    onProgress?: UploadProgressHandler,
  ) => {
    const body = new FormData();
    body.set("version", String(version));
    files.forEach((file) => body.append("files", file, file.name));
    return uploadRequest<{
      document_id: number;
      added_count: number;
      image_ids: number[];
      version: number;
    }>(`/api/admin/enterprise-documents/${id}/images`, body, onProgress);
  },
  deleteEnterpriseImage: (documentId: number, imageId: number, version: number) => request<{
    document_id: number;
    image_id: number;
    deleted: boolean;
    version: number;
    cover_image_id: number | null;
    cleanup_pending: boolean;
  }>(`/api/admin/enterprise-documents/${documentId}/images/${imageId}`, {
    method: "DELETE",
    body: JSON.stringify({ version }),
  }),
  deleteEnterpriseDocument: (id: number) => request<{ id: number; deleted: boolean }>(
    `/api/admin/enterprise-documents/${id}`,
    { method: "DELETE" },
  ),
  socialAccounts: () => request<SocialAccount[]>("/api/social-accounts"),
  adminSocialAccounts: () => request<SocialAccount[]>("/api/admin/social-accounts"),
  createSocialAccount: (values: {
    platform: string;
    displayName: string;
    handle: string;
    description: string;
    profileUrl: string;
    avatar: File | null;
  }) => {
    const body = new FormData();
    body.set("platform", values.platform);
    body.set("display_name", values.displayName);
    body.set("handle", values.handle);
    body.set("description", values.description);
    body.set("profile_url", values.profileUrl);
    if (values.avatar) body.set("avatar", values.avatar, values.avatar.name);
    return uploadRequest<SocialAccount>("/api/admin/social-accounts", body);
  },
  updateSocialAccount: (id: number, body: {
    version: number;
    platform: string;
    display_name: string;
    handle?: string | null;
    description?: string | null;
    profile_url: string;
    published: boolean;
  }) => request<SocialAccount>(`/api/admin/social-accounts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  }),
  updateSocialAvatar: (id: number, version: number, avatar: File) => {
    const body = new FormData();
    body.set("version", String(version));
    body.set("avatar", avatar, avatar.name);
    return uploadRequest<SocialAccount>(`/api/admin/social-accounts/${id}/avatar`, body);
  },
  deleteSocialAccount: (id: number) => request<{ id: number; deleted: boolean }>(
    `/api/admin/social-accounts/${id}`,
    { method: "DELETE" },
  ),
  config: () => request<ClientConfig>("/api/config"),
  mapLocations: () => request<MapLocation[]>("/api/map/locations"),
  adminLocations: () => request<AdminLocation[]>("/api/admin/locations"),
  searchAmap: (q: string, placeType: "community" | "store") => {
    const search = new URLSearchParams({ q, place_type: placeType });
    return request<AmapSearchResult>(`/api/admin/amap/search?${search.toString()}`);
  },
  adminUsers: () => request<AdminUser[]>("/api/admin/users"),
  userCaseAccess: (id: number, q = "") => {
    const search = new URLSearchParams();
    if (q) search.set("q", q);
    return request<CaseAccessResult>(`/api/admin/users/${id}/case-access?${search.toString()}`);
  },
  updateUserCaseAccess: (id: number, caseIds: number[]) =>
    request<{ user_id: number; authorized_count: number }>(
      `/api/admin/users/${id}/case-access`,
      { method: "PATCH", body: JSON.stringify({ case_ids: caseIds }) },
    ),
  caseViews: (params: {
    employee?: string;
    case?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
  }) => {
    const search = new URLSearchParams({
      page: String(params.page ?? 1),
      page_size: "30",
    });
    if (params.employee) search.set("employee", params.employee);
    if (params.case) search.set("case", params.case);
    if (params.dateFrom) search.set("date_from", params.dateFrom);
    if (params.dateTo) search.set("date_to", params.dateTo);
    return request<CaseViewLogPage>(`/api/admin/case-views?${search.toString()}`);
  },
  materialCadPreview: (id: number) => request<{state: string; job_state?: string; message: string; preview_url: string | null; can_generate: boolean}>(`/api/materials/${id}/preview`),
  generateMaterialCadPreview: (id: number) => request(`/api/admin/materials/${id}/preview`, { method: "POST" }),
  generateCadPreview: (id: string) => request(`/api/admin/floorplan-library/files/${id}/preview`, { method: "POST" }),
  browserCadTicket: (id: string) => request<{ticket:string;sha256:string;size:number;runner_url:string}>(`/api/admin/cad-browser/files/${encodeURIComponent(id)}/ticket`, {method:"POST"}),
  floorplanLibrary: () => request<FloorplanLibraryData>("/api/floorplan-library"),
  adminFloorplanLibrary: () => request<FloorplanLibraryData>("/api/admin/floorplan-library"),
  floorplanOrder: (level: ArchiveOrderLevel) => request<ArchiveOrderState>(`/api/admin/floorplan-library/order?level=${level}`),
  updateFloorplanOrder: (
    level: ArchiveOrderLevel,
    ids: string[],
    mode?: ArchiveCategoryMode,
  ) => request<{ archive_kind: OrderArchiveKind; level: ArchiveOrderLevel; mode: ArchiveCategoryMode; ids: string[] }>("/api/admin/floorplan-library/order", {
    method: "PATCH",
    body: JSON.stringify({ level, ids, mode }),
  }),
  uploadFloorplan: (values: {
    file: File;
    title: string;
    community: string;
    kinds: FloorplanKind[];
    householdTags: string;
    building: string;
    remark: string;
    onProgress?: UploadProgressHandler;
  }) => {
    const body = new FormData();
    body.set("file", values.file);
    body.set("title", values.title);
    body.set("community", values.community);
    body.set("kinds", JSON.stringify(values.kinds));
    body.set("household_tags", values.householdTags);
    body.set("building", values.building);
    body.set("remark", values.remark);
    return uploadRequest<FloorplanFile>("/api/admin/floorplan-library/upload", body, values.onProgress).then(result => {
      if (result.category === "drawing" && result.size_bytes <= 16*1024*1024) window.dispatchEvent(new CustomEvent("cad-browser-request", {detail:{id:result.id,title:result.title}}));
      return result;
    });
  },
  updateFloorplan: (id: string, body: {
    version: number;
    title: string;
    community: string;
    category: "drawing" | "other";
    kinds: FloorplanKind[];
    household_tags: string[];
    building: string | null;
    remark: string | null;
  }) => request<FloorplanFile>(`/api/admin/floorplan-library/files/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  }),
  deleteFloorplan: (id: string, version: number) =>
    request<{ id: string; deleted: boolean; origin: "library" | "managed" }>(
      `/api/admin/floorplan-library/files/${id}`,
      { method: "DELETE", body: JSON.stringify({ version }) },
    ),
  system: () => request<SystemStatus>("/api/admin/system"),
  createUser: (body: {
    username: string;
    display_name: string;
    password: string;
    role: "admin" | "employee";
    access_level: "full" | "restricted";
  }) =>
    request<{ id: number }>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateUser: (
    id: number,
    body: {
      username: string;
      display_name: string;
      password?: string;
      role: "admin" | "employee";
      access_level: "full" | "restricted";
    },
  ) =>
    request<{
      id: number;
      username: string;
      display_name: string;
      role: "admin" | "employee";
      access_level: "full" | "restricted";
      account_type: "admin" | "employee" | "restricted";
      sessions_revoked: boolean;
    }>(`/api/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteUser: (id: number) =>
    request<{ id: number; deleted: boolean; sessions_revoked: boolean }>(
      `/api/admin/users/${id}`,
      { method: "DELETE" },
    ),
  setUserDisabled: (id: number, disabled: boolean) =>
    request<{ id: number; disabled: boolean }>(`/api/admin/users/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ disabled }),
    }),
  saveLocationCandidate: (
    id: number,
    body: {
      expected_revision: number;
      poi_id: string;
      address: string;
      district: string;
      adcode: string;
      lng: number;
      lat: number;
      evidence_url: string;
      evidence_note: string;
    },
  ) =>
    request<{ id: number; status: string; revision: number }>(
      `/api/admin/locations/${id}/candidate`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  verifyLocation: (id: number, expectedRevision: number) =>
    request<{ id: number; status: string; revision: number }>(
      `/api/admin/locations/${id}/verify`,
      { method: "POST", body: JSON.stringify({ expected_revision: expectedRevision }) },
    ),
};
